import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { useOnScreen } from 'client/site-modules/shared/hooks/use-on-screen';
import { Carousel } from 'client/site-modules/shared/components/carousel/carousel';
import { Link } from 'site-modules/shared/components/link/link';

import './cards-carousel.scss';

function renderComponent(entry, CardComponent, otherProps) {
  const RenderedComponent = CardComponent;
  return (
    <div key={entry.id} className="col">
      <RenderedComponent entry={entry} content={entry} {...otherProps} />
    </div>
  );
}

// eslint-disable-next-line react/prop-types
export function CardsCarouselSlide({ entry, CardComponent, ...props }) {
  const ref = useRef();
  // We set rootMargin here to track the horizontal visibility only
  const [isVisible] = useOnScreen(ref, { threshold: 0.5, rootMargin: '4000px 0px 4000px 0px' });

  return (
    <div ref={ref} className="cards-carousel-slide col px-0_5">
      {renderComponent(entry, CardComponent, { ...props, isVisible })}
    </div>
  );
}

export function CardsCarousel({
  content = DEFAULT_CONTENT,
  CardComponent,
  childrenFilter = null,
  slidesToShowDesktop = 3,
  slidesToShowMobile = 1,
  slidesToScrollDesktop = null,
  slidesToScrollMobile = null,
  forceCarousel = false,
  showArrows = false,
  centerMode = false,
  infinite = false,
  initialSlide = null,
  isMobile = false,
  excoBuyingGuideEnabled,
  ...props
}) {
  let cardsEntries = content.children();
  if (childrenFilter) {
    cardsEntries = cardsEntries.filter(child => childrenFilter(child, { isMobile }));
  }
  const isOneSlideToShowOnMobile = slidesToShowMobile === 1;
  const slidesToShow = isMobile ? slidesToShowMobile : slidesToShowDesktop;

  const moreLinkText = content.metadata('moreLinkText').value();
  const moreLink = content.metadata('moreLink').value();

  const defaultInitialSlide = isMobile && !isOneSlideToShowOnMobile ? slidesToShowMobile - 1 : 0;
  const cardsToDisplay = cardsEntries.filter(entry => !entry.hasMetadata('adType'));

  return (
    <Fragment>
      {slidesToShow >= cardsEntries.length && !forceCarousel ? (
        <div className="cards-flex row flex-nowrap">
          {cardsToDisplay.map(entry => renderComponent(entry, CardComponent, props))}
        </div>
      ) : (
        <Carousel
          swipe
          arrows={showArrows}
          infinite={infinite}
          slidesToShow={slidesToShowDesktop}
          slidesToScroll={slidesToScrollDesktop || slidesToShowDesktop}
          centerMode={centerMode}
          centerPadding="50% 0 0"
          initialSlide={initialSlide !== null ? initialSlide : defaultInitialSlide}
          responsive={[
            {
              breakpoint: 480,
              settings: {
                slidesToShow: slidesToShowMobile,
                slidesToScroll: slidesToScrollMobile || slidesToShowMobile,
              },
            },
          ]}
          className="cards-carousel"
        >
          {cardsToDisplay.map(entry => (
            <CardsCarouselSlide
              key={entry.id}
              entry={entry}
              CardComponent={CardComponent}
              isMobile={isMobile}
              {...props}
            />
          ))}
        </Carousel>
      )}
      {!!moreLink && !!moreLinkText && (
        <Link className="d-inline-block text-primary-darker mt-1_5" to={moreLink}>
          {moreLinkText}
          <i className="icon-arrow-right4 small ms-0_25" aria-hidden />
        </Link>
      )}
    </Fragment>
  );
}

CardsCarousel.propTypes = {
  content: CmsEntities.Content,
  CardComponent: PropTypes.func.isRequired,
  childrenFilter: PropTypes.func,
  slidesToShowDesktop: PropTypes.number,
  slidesToShowMobile: PropTypes.number,
  slidesToScrollDesktop: PropTypes.number,
  slidesToScrollMobile: PropTypes.number,
  initialSlide: PropTypes.number,
  forceCarousel: PropTypes.bool,
  showArrows: PropTypes.bool,
  centerMode: PropTypes.bool,
  infinite: PropTypes.bool,
  isMobile: PropTypes.bool,
};
